import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { login, verifyMfaLogin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok && res.mfaRequired) { setMfaStep(true); return; }
    if (res.ok) navigate(res.data?.must_change_password ? "/change-password" : "/");
    else setError(res.error);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await verifyMfaLogin(mfaCode.trim(), useRecovery ? "recovery" : "totp");
    setLoading(false);
    if (res.ok) navigate(res.data?.must_change_password ? "/change-password" : "/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#17324D]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-center p-12 xl:p-16 text-slate-200 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#315A7D]/40 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-[#B28A3D]/15 blur-3xl" />
        <div className="relative flex max-w-md flex-col gap-6" data-testid="login-hero-group">
          <img src="/fo-logo-light-trimmed.png" alt="Fortunate Operations" className="block w-full max-w-[420px] xl:max-w-[480px] h-auto object-contain" />
          <div>
            <div className="gold-rule mb-4" />
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">
              The operations console for modern insurance teams.
            </h1>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              Run your entire pipeline — from BPO intake and quoting through underwriting,
              issued policies, commissions and retention follow-ups — in one command center.
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-amber-300/90">
              <ShieldCheck className="h-4 w-4 text-[#B28A3D]" />
              Secure RBAC access · encrypted credential vault
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-12 xl:left-16 text-[11px] text-slate-500">© Fortunate Operations</div>
      </div>

      {/* Right login form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 inline-flex items-center rounded-lg bg-[#17324D] px-4 py-3 shadow-sm">
            <img src="/fo-logo-light-trimmed.png" alt="Fortunate Operations" className="w-full max-w-[240px] h-auto object-contain" />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{mfaStep ? t("mfa.title") : t("login.title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{mfaStep ? t("mfa.prompt") : "Sign in to the Agency OS to access your role-based workspace."}</p>

          {!mfaStep ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label>{t("login.email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t("login.password")}</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">{t("login.forgot")}</Link>
              </div>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? t("common.loading") : t("login.submit")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </form>
          ) : (
          <form onSubmit={submitMfa} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label>{useRecovery ? "Recovery code" : "Authentication code"}</Label>
              <Input data-testid="mfa-code" autoFocus value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"} required />
            </div>
            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200" data-testid="mfa-error">{error}</div>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="mfa-submit">
              {loading ? "Verifying…" : "Verify"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <button type="button" className="w-full text-xs text-primary hover:underline" data-testid="mfa-toggle-recovery" onClick={() => { setUseRecovery((v) => !v); setMfaCode(""); setError(""); }}>
              {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
